<script setup lang="ts">
import { inject, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";

import type { BreadcrumbItem } from "@/components/ui/BreadcrumbNavigation.vue";
import { lastErrorKey } from "@/injectionKeys";
import AppToolbar from "@/layouts/AppToolbar.vue";
import FullPageCenteredLayout from "@/layouts/FullPageCenteredLayout.vue";
import { useRemainingHeight } from "@/services/remainingHeight";

const { breadcrumbItems } = defineProps<{ breadcrumbItems: BreadcrumbItem[] }>();
const { computeRemainingHeight } = useRemainingHeight(".default-page");
const bottomBanner = useTemplateRef<HTMLDivElement>("bottomBanner");
const bottomBannerHeight = ref(0);

const lastError = inject(lastErrorKey);

const bottomBannerResizeObserver = new ResizeObserver((entries) => {
  const [banner] = entries;
  if (banner) {
    const first = banner.borderBoxSize[0];
    if (first) {
      bottomBannerHeight.value = first.blockSize;
    }
  }
});

onMounted(() => {
  if (bottomBanner.value) {
    bottomBannerResizeObserver.observe(bottomBanner.value);
  }
});

onBeforeUnmount(() => {
  if (bottomBanner.value) {
    bottomBannerResizeObserver.unobserve(bottomBanner.value);
  }
});
</script>

<template>
  <div class="default-layout-container">
    <AppToolbar :breadcrumb-items="breadcrumbItems" @vue:mounted="computeRemainingHeight" />
    <FullPageCenteredLayout v-if="lastError">
      The resource you're looking for does not exist.
    </FullPageCenteredLayout>
    <main v-else class="default-page">
      <slot></slot>
    </main>
    <div v-if="$slots.bottomBanner && !lastError" class="bottom-banner" ref="bottomBanner">
      <slot name="bottomBanner" />
    </div>
  </div>
</template>

<style scoped>
.default-layout-container {
  .app-toolbar {
    position: absolute;
    z-index: 7000;
    top: 0;
  }

  .default-page {
    z-index: 1;
    height: 100dvh;
    padding: 0 var(--page-padding);
    padding-top: var(--numbers-56);
  }

  .bottom-banner {
    position: fixed;
    z-index: 2;
    bottom: 0;
    width: 100dvw;
    padding: var(--numbers-12) var(--page-padding);
    border-top: solid var(--stroke-width-md) var(--border-tertiary);
    background-color: var(--background-primary);
  }
}
</style>
